import React, { useEffect, useRef, useState } from 'react';
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop';
import { PreviewSetting } from './PreviewSetting';
import * as S from './PreviewTab.styled';
async function calculateHmac(hmacKey, shopId, supporterId, tierId, timestamp) {
    if (!crypto?.subtle) {
        throw new Error('Crypto is not supported in this browser');
    }
    const message = `shop_id=${shopId}&supporter_id=${supporterId}&tier_id=${tierId}&timestamp=${timestamp}`;
    // Import the HMAC key
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(hmacKey), { name: 'HMAC', hash: 'SHA-512' }, false, ['sign']);
    // Sign the message
    const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message));
    // Convert to hex string
    return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
export const PreviewTab = ({ settings, iframeUrl, hmac }) => {
    const iframeContainerRef = useRef(null);
    const { currentShop } = useCurrentShop();
    const [queryParams, setQueryParams] = useState('');
    const [queryParamValues, setQueryParamValues] = useState({});
    const [membershipType, setMembershipType] = useState('none');
    useEffect(() => {
        const calculateQp = async () => {
            if (!currentShop)
                return;
            const timestamp = (Date.now() / 1000).toString();
            // Determine supporter and tier IDs based on membership type
            let supporterId = '';
            let tierId = '';
            switch (membershipType) {
                case 'none':
                    supporterId = 'none';
                    tierId = 'none';
                    break;
                case 'free':
                    supporterId = 'free-supporter-123';
                    tierId = 'none';
                    break;
                case 'paid':
                    supporterId = 'paid-supporter-789';
                    tierId = 'premium-tier-101';
                    break;
            }
            const hmacSignature = await calculateHmac(hmac, currentShop.id, supporterId, tierId, timestamp);
            const baseParams = {
                hmac: hmacSignature,
                shop_id: currentShop.id,
                timestamp,
                ...queryParamValues,
            };
            // Only add supporter and tier IDs if they exist
            if (supporterId) {
                baseParams.supporter_id = supporterId;
            }
            if (tierId) {
                baseParams.tier_id = tierId;
            }
            const qp = new URLSearchParams(baseParams).toString();
            setQueryParams(qp);
        };
        calculateQp();
    }, [hmac, currentShop?.id, queryParamValues, membershipType]);
    return (React.createElement(S.PreviewContainer, null,
        React.createElement(S.SettingsLabel, null, "Settings"),
        React.createElement(S.PreviewWrapper, null,
            React.createElement(S.Preview, null, settings.map((setting) => {
                return (React.createElement(PreviewSetting, { key: setting.id, setting: {
                        ...setting,
                        value: '',
                    }, onSettingUpdate: (k, v) => {
                        setQueryParamValues({
                            ...queryParamValues,
                            [k]: v?.toString() || '',
                        });
                    } }));
            })),
            React.createElement(S.IframeContainer, { ref: iframeContainerRef },
                React.createElement(S.ScaledIframe, { src: `${iframeUrl.trim()}?${queryParams}`, scaleRatio: 0.5 }))),
        React.createElement(S.MembershipToggle, null,
            React.createElement(S.MembershipLabel, null, "Membership Type:"),
            React.createElement(S.MembershipButtons, null,
                React.createElement(S.MembershipButton, { "$active": membershipType === 'none', onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMembershipType('none');
                    } }, "None"),
                React.createElement(S.MembershipButton, { "$active": membershipType === 'free', onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMembershipType('free');
                    } }, "Free"),
                React.createElement(S.MembershipButton, { "$active": membershipType === 'paid', onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMembershipType('paid');
                    } }, "Paid")))));
};
